import React from 'react';
import {Quill} from 'react-quill';

import ReactDOMServer from 'react-dom/server';

import {CompanyRegularIcon} from 'shells/icon/svg/company-regular-icon';
import {PersonCircleRegularIcon} from 'shells/icon/svg/person-circle-regular-icon';

const Embed = Quill.import('blots/embed');

export type Placeholder = {
    id: string;
    label: string;
    fallback?: string;
    entityType?: string;
};

// Renders for custom field and named field merge tags
export class PlaceholderBlot extends Embed {
    static blotName = 'placeholder';
    static tagName = 'span';
    static className = 'ql-placeholder-blot';

    placeholderValue: Placeholder | null | undefined;

    constructor(domNode: Node, value?: Placeholder) {
        super(domNode, value);
        this.placeholderValue = value;
    }

    static create(value: Placeholder) {
        const node: HTMLElement = super.create(value);
        const showFallback = value.fallback;

        node.setAttribute('data-id', value.id);
        node.setAttribute('data-label', value.label);
        node.setAttribute('spellcheck', 'false');
        // If entityType is 'undefined', we show it as an error placeholder
        node.setAttribute('data-entity-type', value.entityType ?? 'undefined');

        if (showFallback) {
            node.setAttribute('data-fallback', value.fallback ?? '');
        }

        const fallback = showFallback ? ` | "${value.fallback}"` : '';

        let icon = undefined;
        switch (value.entityType) {
            case 'Contacts':
                icon = PersonCircleRegularIcon;
                break;
            case 'Accounts':
                icon = CompanyRegularIcon;
                break;
        }

        node.innerHTML = ReactDOMServer.renderToStaticMarkup(
            React.createElement(
                'span',
                {className: 'ql-placeholder-content'},
                icon
                    ? React.createElement(icon, {
                          size: 14,
                          wrapWithDiv: false,
                      })
                    : null,
                React.createElement(
                    'span',
                    {
                        className: value.entityType === 'undefined' ? 'strikethrough' : undefined,
                    },
                    value.label
                ),
                React.createElement('span', {}, fallback)
            )
        );

        return node;
    }

    value() {
        return this.placeholderValue ? `{${this.placeholderValue.id}}` : null;
    }

    static value(domNode: HTMLElement) {
        return domNode.dataset;
    }

    length(): number {
        return 1;
    }
}
